import { DateTime, Duration } from 'luxon';
import React from 'react';
import {EnvironmentDataInterface, EnvironmentSource, LayerType} from '../utils/EnvironmentDataHandler';
import MultiscaleTimeAxis from '../utils/MultiscaleTimeAxis';
import { SelectionInterval } from '../utils/SelectionInterval';
import { Domain } from './Constants';

import './EnvironmentWidget.css';

interface EnvironmentWidgetProps {
    interval: SelectionInterval;
    timeline_taxis: MultiscaleTimeAxis
    is_animating: boolean
    cday_offset: Duration
    left: number
    right: number

    sel_data_time: DateTime | null;
    env_data_times: DateTime[];
    env_handler: EnvironmentDataInterface | null;
    env_type: LayerType | null;
    env_src: EnvironmentSource | null;
    env_intv_dur_cutoff: Duration

    sel_domain: Domain;
    
    onenvlayerchange: (new_src: EnvironmentSource | null, new_type: LayerType | null) => void;
    onenvtimechange: (new_time: DateTime) => void;
    onanimtoggle: () => void
    onanimspeedchange: (amount: number) => void;
}

class EnvironmentWidget extends React.Component<EnvironmentWidgetProps> {
    constructor(props: EnvironmentWidgetProps) {
        super(props);
    }

    updateSelection(ev: React.ChangeEvent<HTMLInputElement>) {
        if (ev.nativeEvent.target === null) {
            return;
        }

        if (!(ev.nativeEvent.target instanceof HTMLInputElement)) {
            throw "updateSelection called from an element other than an input element";
        }

        const timestep_reanalysis = this.props['env_handler'] === null ? null : this.props['env_handler'].getAnalysisTimeStep();
        const dt_reanal = timestep_reanalysis === null ? null : timestep_reanalysis.as('hours');

        let anim_dts = this.props['env_data_times'].slice();
        anim_dts.sort((a, b) => a.toMillis() - b.toMillis());

        const new_val = Number(ev.nativeEvent.target.value);
        const new_dt = dt_reanal === null ? null : anim_dts[0].plus({'hours': new_val * dt_reanal});
        if (this.props['sel_data_time'] !== null && new_dt !== null && this.props['onenvtimechange'] !== undefined 
            && new_dt.toMillis() != this.props['sel_data_time'].toMillis()) {
            this.props['onenvtimechange'](new_dt);
        }
    }

    animToggle() {
        if (this.props['onanimtoggle'] !== undefined) {
            this.props['onanimtoggle']();
        }
    }

    animSpeedChange(amount: number) {
        if (this.props['onanimspeedchange'] !== undefined) {
            this.props['onanimspeedchange'](amount);
        }
    }

    layerChange() {
        if (this.props['onenvlayerchange'] !== undefined) {
            const sel_elem: HTMLElement | null = document.getElementById('layer-selection');
            if (sel_elem === null || !(sel_elem instanceof HTMLSelectElement)) {
                throw "#layer-selection is missing or the wrong type of element";
            }

            const ts_value = sel_elem.value as LayerType | 'null';;
            if (ts_value == 'null') {
                this.props['onenvlayerchange'](null, null);
            }
            else {
                const [new_src, new_type] = ts_value.split(':');
                this.props['onenvlayerchange'](new_src as EnvironmentSource, new_type as LayerType);
            }
        }
    }

    render() {
        const t_scale = this.props['timeline_taxis'];

        let anim_dts = this.props['env_data_times'].slice();
        anim_dts.sort((a, b) => a.toMillis() - b.toMillis());

        const dx = anim_dts.length < 2 ? 0 : t_scale.map(anim_dts[1]) - t_scale.map(anim_dts[0]);
        const left = anim_dts.length < 1 ? 0 : t_scale.map(anim_dts[0]);

        let marker_display: JSX.Element;
        const marker_display_cls = this.props['right'] - this.props['left'] > 500 ? '' : 'mobile';
        const dt_reanal = this.props['env_handler'] === null ? null : this.props['env_handler'].getAnalysisTimeStep()

        if (this.props['sel_data_time'] !== null && dt_reanal !== null && this.props['interval'].duration() <= this.props['env_intv_dur_cutoff']) {
            const dt_reanal_hrs = dt_reanal.as('hours');
            const val = this.props['sel_data_time'].diff(anim_dts[0], 'hours').as('hours') / dt_reanal_hrs;
            marker_display = <input id='t-slider' type="range"
                                style={{'left': (left - 9) + 'px', 'width' : (dx * (anim_dts.length - 1) + 19) + 'px'}}
                                min="0" max={anim_dts.length - 1} step="1" value={val} onChange={this.updateSelection.bind(this)}/>
        }
        else if (this.props['env_handler'] === null) {
            marker_display = <div id='env-message'>
                                Environment data are off; select from the menu on the left to turn them on
                            </div>
        }
        else if (this.props['interval'].duration() > this.props['env_intv_dur_cutoff']) {
            marker_display = <div id='env-message'>
                                Select an interval shorter than {this.props['env_intv_dur_cutoff'].hours} hrs to display environment data
                            </div>
        }
        else if (!['North America', 'Europe'].includes(this.props['sel_domain'])) {
            marker_display = <div id='env-message'>
                                No environment data available on the {this.props['sel_domain']} domain
                            </div>
        }
        else {
            marker_display = <div id='env-message'>
                                No environment data available
                            </div>
        }

        const default_value = this.props['env_type'] === null ? 'null' : this.props['env_src'] + ':' + this.props['env_type'];

        let playpause_class = this.props['is_animating'] ? 'active' : '';
        let button_class = '';
        let playpause_click = this.animToggle.bind(this);
        let animspdup_click = () => this.animSpeedChange(1);
        let animspddn_click = () => this.animSpeedChange(-1);

        if (this.props['env_data_times'].length == 0) {
            playpause_class = 'disabled';
            button_class = 'disabled';
            playpause_click = () => {};
            animspdup_click = () => {};
            animspddn_click = () => {};
        }

        return (
            <div id='env-controls'>
                <div id='layer-controls'>
                    <span>Reanalysis: {this.props['env_src'] === null ? 'Off' : this.props['env_src'].toUpperCase()}</span>
                    <select id='layer-selection'
                        onChange={this.layerChange.bind(this)}
                        defaultValue={default_value}>
                        <option value='null'>None</option>
                        {EnvironmentDataInterface.getAvailableReanalyses().map(rea => {
                            return (
                                <optgroup key={rea} label={rea.toUpperCase()}>
                                    {EnvironmentDataInterface.getAvailableTypes().map(typ => {
                                    return (
                                        <option key={rea + '-' + typ} value={rea + ':' + typ}>
                                            {EnvironmentDataInterface.getDropdownEntry(typ)}
                                        </option>);
                                    })}
                                </optgroup>
                            )
                        })}
                    </select>
                </div>
                <div id='anim-controls'>
                    <div id='playpause'
                        className={playpause_class}
                        onClick={playpause_click}>&#9654;&#xfe0e;&#9616;&#9616;
                    </div>
                    <div
                        className={button_class}
                        onClick={animspddn_click}>-</div>
                    <div
                        className={button_class}
                        onClick={animspdup_click}>+</div>
                </div>
                <div id='marker-display' className={marker_display_cls}>
                    {marker_display}
                </div>
            </div>
        )
    }
}

export default EnvironmentWidget;